import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BadgeCheck, Ban, Check, CheckCircle2, Clock, Copy, Search } from "lucide-react";
import { Pill, PdLoading, copyText, fmtIst, mask } from "@/components/PdPill";

const DATES = [["today", "Today"], ["yesterday", "Yesterday"], ["last7", "Last 7d"], ["all", "All"]];
const shortName = (n) => {
  const s = (n || "").split(/\s+/).slice(0, 2).join(" ");
  return s.length > 18 ? `${s.slice(0, 18)}…` : s;
};

export function DmQueue({ tab, data, date, setDate, hide, onMark, busy }) {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [sub, setSub] = useState("open");
  const agency = params.get("agency") || "all";
  const showResolved = tab === "complaint" && sub === "resolved";
  const chips = useMemo(() => {
    if (!data) return [];
    const by = Object.fromEntries(data.agencies.map((a) => [a.group_id, a]));
    return (data.logins || []).map((l) => ({ id: l.group_id, name: l.name, n: (by[l.group_id]?.[tab] || []).length }));
  }, [data, tab]);
  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    const src = showResolved
      ? (data.resolved_recent || []).filter((r) => agency === "all" || r.group_id === agency)
      : data.agencies.filter((a) => agency === "all" || a.group_id === agency).flatMap((a) => a[tab] || []);
    return src.filter((r) => !needle || r.lpg_id.includes(needle) || (r.mobile || "").includes(needle) || (r.dac_code || "").includes(needle) || r.agency.toLowerCase().includes(needle));
  }, [data, tab, agency, q, showResolved]);
  if (!data) return <PdLoading />;
  const total = chips.reduce((s, c) => s + c.n, 0);
  const pick = (id) => setParams(id === "all" ? {} : { agency: id }, { replace: true });
  return (
    <div className="space-y-4 animate-in fade-in duration-300" data-testid={`dm-queue-${tab}`}>
      <div className="space-y-2.5">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {DATES.map(([k, l]) => <Pill key={k} tone="brown" on={date === k} onClick={() => setDate(k)} testid={`dm-date-${k}`}>{l}</Pill>)}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8A8A80]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search LPG ID / mobile / DAC…" data-testid="dm-search"
            className="w-full h-11 pl-10 pr-3 text-sm bg-white border border-[#E0DFD6] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#385C4B]/30" />
        </div>
        <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 pb-1" data-testid="dm-agency-chips">
          <Pill on={agency === "all"} onClick={() => pick("all")} testid="dm-chip-all">All {total}</Pill>
          {chips.map((c) => <Pill key={c.id} on={agency === c.id} onClick={() => pick(c.id)} testid={`dm-chip-${c.id}`}>{shortName(c.name)} {c.n}</Pill>)}
        </div>
      </div>
      {tab === "complaint" && (
        <div className="grid grid-cols-2 p-1 rounded-xl bg-white border border-[#E5E5DF]" role="tablist" data-testid="dm-complaint-subtabs">
          {[["open", `Open ${total}`], ["resolved", `Resolved ${(data.resolved_recent || []).length}`]].map(([k, l]) => (
            <button key={k} role="tab" aria-selected={sub === k} onClick={() => setSub(k)} data-testid={`dm-complaint-${k}`}
              className={`h-10 rounded-lg text-sm font-semibold transition-colors ${sub === k ? "bg-[#385C4B] text-white shadow-sm" : "text-[#3A3A35]"}`}>
              {l}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.2em] text-[#6B6B61] font-semibold" data-testid="dm-queue-count">{rows.length} {showResolved ? "resolved · last 7 days" : tab}</span>
        {rows.length > 0 && !showResolved && (
          <button onClick={() => copyText(rows.map((r) => r.lpg_id).join("\n"), `${rows.length} IDs`)} data-testid="dm-copy-all-ids"
            className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-full border border-[#E0DFD6] bg-white hover:bg-[#EFEEE6] transition-colors">
            <Copy className="w-3.5 h-3.5" />Copy IDs
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#DCDCD3] bg-white/60 p-8 text-center" data-testid="pd-empty">
          <CheckCircle2 className="w-8 h-8 mx-auto text-[#385C4B]/50" />
          <p className="mt-2 text-sm text-[#6B6B61]">{showResolved ? "Pichhle 7 din mein koi complaint resolve nahi hua." : `Koi ${tab} delivery nahi mili.`}</p>
        </div>
      ) : showResolved ? (
        <div className="space-y-3">{rows.map((r) => <ResolvedCard key={r.key} r={r} hide={hide} />)}</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const k = `${r.message_id}:${r.entry_index}`;
            return <DeliveryCard key={k} r={r} tab={tab} hide={hide} busy={busy === k} onMark={onMark} />;
          })}
        </div>
      )}
    </div>
  );
}

function ResolvedCard({ r, hide }) {
  return (
    <article className="bg-white rounded-2xl border border-[#E5E5DF] shadow-sm p-4 space-y-2.5" data-testid={`dm-resolved-${r.lpg_id}`}>
      <div className="flex items-center gap-2">
        <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#6B6B61] truncate">{r.agency}</span>
        <span className="ml-auto shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#2E6B8A] bg-[#2E6B8A]/10 px-2 py-0.5 rounded-full">
          <BadgeCheck className="w-3 h-3" />Resolved
        </span>
      </div>
      <button onClick={() => copyText(r.lpg_id, "LPG ID")} className="w-full flex items-center justify-between gap-3 rounded-xl bg-[#F4F3EC] px-3.5 py-2.5" data-testid={`dm-resolved-copy-${r.lpg_id}`}>
        <span className="font-mono text-base font-bold tracking-wider text-[#1A1A1A] truncate">{mask(r.lpg_id, hide)}</span>
        <Copy className="w-4 h-4 text-[#385C4B] shrink-0" />
      </button>
      <div className="text-sm rounded-lg bg-[#2E6B8A]/10 text-[#22546D] px-3 py-2 break-words" data-testid={`dm-resolved-note-${r.lpg_id}`}>{r.resolution_note}</div>
      <div className="text-xs text-[#6B6B61]">DAC {r.dac_code || "—"} · by {r.resolved_by} · {fmtIst(r.resolved_at)}</div>
    </article>
  );
}

const ACTS = [
  ["correct", "Done", Check, "bg-[#385C4B] text-white hover:bg-[#2D4A3C]", "pd-done"],
  ["wrong", "DAC ✗", null, "bg-[#E89B35]/15 text-[#A8620F] border border-[#E89B35]/60 hover:bg-[#E89B35]/25", "pd-dac-wrong"],
  ["cancelled", "Cancelled", Ban, "bg-[#8A6046]/10 text-[#8A6046] border border-[#8A6046]/40 hover:bg-[#8A6046]/20", "pd-cancel"],
];
const chip = "inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#F4F3EC] text-sm text-[#1A1A1A] active:scale-95 transition-transform";

export function DeliveryCard({ r, tab, hide, busy, onMark }) {
  return (
    <article className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${r.urgent ? "border-[#D9534F]/40" : "border-[#E5E5DF]"}`} data-testid={`pd-row-${r.lpg_id}`}>
      {r.urgent && <div className="h-1 bg-[#D9534F]" />}
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#6B6B61] truncate">{r.agency}</span>
          {r.urgent && <span className="ml-auto shrink-0 text-[10px] font-bold uppercase tracking-wider text-white bg-[#D9534F] px-2 py-0.5 rounded-full" data-testid={`dm-urgent-${r.lpg_id}`}>Urgent</span>}
        </div>
        <button onClick={() => copyText(r.lpg_id, "LPG ID")} data-testid={`pd-copy-lpg-${r.lpg_id}`}
          className="w-full flex items-center justify-between gap-3 rounded-xl bg-[#F4F3EC] px-3.5 py-3 active:scale-[0.99] transition-transform">
          <span className="font-mono text-lg font-bold tracking-wider text-[#1A1A1A] truncate" data-testid="pd-lpg-text">{mask(r.lpg_id, hide)}</span>
          <Copy className="w-4 h-4 text-[#385C4B] shrink-0" />
        </button>
        <div className="flex flex-wrap gap-2">
          {r.dac_code && (
            <button onClick={() => copyText(r.dac_code, "DAC")} className={chip} data-testid={`dm-copy-dac-${r.lpg_id}`}>
              <span className="text-xs text-[#6B6B61]">DAC</span><b className="font-mono">{r.dac_code}</b><Copy className="w-3.5 h-3.5 text-[#6B6B61]" />
            </button>
          )}
          {r.mobile && (
            <button onClick={() => copyText(r.mobile, "mobile")} className={chip} data-testid={`dm-copy-mobile-${r.lpg_id}`}>
              <span className="font-mono">{r.mobile}</span><Copy className="w-3.5 h-3.5 text-[#6B6B61]" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-[#6B6B61]"><Clock className="w-3.5 h-3.5" />Dispatched {fmtIst(r.created_at)}</div>
        {tab === "complaint" && (
          <div className="text-xs rounded-lg bg-[#FDECEA] text-[#B23B37] px-3 py-2" data-testid={`dm-complaint-info-${r.lpg_id}`}>
            DAC ✗ · {r.marked_by} · {fmtIst(r.marked_at)}
          </div>
        )}
      </div>
      {tab === "pending" && (
        <div className="grid grid-cols-3 gap-2 px-4 pb-4">
          {ACTS.map(([st, label, Icon, cls, tid]) => (
            <button key={st} disabled={busy} onClick={() => onMark(r, st)} data-testid={`${tid}-${r.lpg_id}`}
              className={`h-12 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-95 transition-[transform,background-color] ${cls}`}>
              {busy && st === "correct" ? "…" : <>{Icon && <Icon className="w-4 h-4" />}{label}</>}
            </button>
          ))}
        </div>
      )}
    </article>
  );
}
